import axios from 'axios'
import { useAppState } from "../redux"
import { useMutation, useQueryClient } from 'react-query'
import { QueryKeys } from '../../constants/query-keys'
import { notifications } from '@mantine/notifications'
import moment from 'moment';
import fileDownload from 'js-file-download';
import { UploadError } from '../../components/GlobalJobMatching/GlobalJobMatchingVersioning/types/versions'

type UploadMatchPriorityPayload = {
  versionId: string
  file: any
}

export const useUploadMatchPriorities = (versionId: string | number, setErrors: (errors: { fileName: string, rowCount: number, rows: any[] } | null) => void) => {
  const appState = useAppState()
  const { csrf_token, csrf_timestamp, organization } = appState.session
  const { code } = organization
  const queryClient = useQueryClient()

  const fileUploadNewMatchPriority = async ({ versionId, file }: UploadMatchPriorityPayload) => {
    const formData = new FormData();
    formData.append('import_mode', 'erase_reload');
    formData.append('table_name', 'org_job_survey_job_map');
    formData.append('pricing_version_id', versionId);
    formData.append('csrf_token', csrf_token);
    formData.append('csrf_timestamp', csrf_timestamp.toString());
    formData.append('the_file', file);

    const response = await axios.post(
      `/${code}/merit-${code}/merit-admin/import/file-upload-new-match-priority`,
      formData,
      { headers: { 'Content-Type': 'text/csv' } }
    );

    if (response.data?.success) {
      await axios.get(`/${code}/merit-${code}/merit-admin/import/${response.data?.msg}`);
    } else {
      throw new Error('Error importing match priority upload');
    }

    return 'OK'
  }

  const mutation = useMutation(fileUploadNewMatchPriority, {
    onMutate: () => setErrors(null),
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.GLO_JAM_PRICING_VERSION, { id: versionId?.toString() }])
      queryClient.invalidateQueries([QueryKeys.GLO_JAM_MATCH_PRIORITIES])
    },
    onError: async (_, { file }) => {
      const getErrorsResponse = await axios.get(`/${code}/app/api/market-pricing/global-job-pricing/getUploadErrors?csrf_token=${csrf_token}&csrf_timestamp=${csrf_timestamp}`)
        .catch(err => console.error('Error getting upload errors: ', err))
      setErrors({ fileName: file.name, ...getErrorsResponse?.data?.response })
      notifications.show({ title: 'Error', message: 'Failed uploading file.', color: 'red' })
    }
  })

  return mutation
}

export const useDownloadFileUploadErrors = () => {
  const appState = useAppState()
  const { csrf_token, csrf_timestamp, organization } = appState.session
  const { code } = organization

  const downloadUploadErrorsToExcel = async (rows: UploadError[]): Promise<void> => {
    try {
      const response = await axios.post(`/${code}/app/api/market-pricing/global-job-pricing/downloadGJMUploadErrors`, {
          rows,
          csrf_token,
          csrf_timestamp
        }, {
          responseType: 'arraybuffer'
        }
      );
      fileDownload(response.data, `upload-errors-${moment().format('MM-DD-YYYY')}.xlsx`);
    } catch (e) {
      console.error('Errors file download error: ', e);
      notifications.show({ title: 'Error', message: 'Failed download errors file.', color: 'red' })
    }
  }

  return useMutation(downloadUploadErrorsToExcel)
}
